import { createFileRoute, Outlet } from '@tanstack/react-router'
import { trpc } from '@/main'
import { noop, useSuspenseQuery } from '@tanstack/react-query'
import { useLingui } from '@lingui/react/macro'
import { Badge } from '@/components/ui/badge'
import { SettingsIcon, UserIcon, BuildingIcon, BriefcaseIcon, FlaskConicalIcon } from 'lucide-react'
import { SegmentedControl, SegmentedTab } from '@/components/custom/segmented-control'
import { Skeleton } from '@/components/ui/skeleton'

// `$appId` is a layout: each management section (settings/applicants/companies/jobs) and the
// interactive playground is its own child route. The credential prefetch lives here so every child
// shares it (TanStack runs every matched route's loader); the tab bar below navigates between them.
const SECTION_ROUTES = {
  settings: '/account/api/$appId/settings',
  applicants: '/account/api/$appId/applicants',
  companies: '/account/api/$appId/companies',
  jobs: '/account/api/$appId/jobs',
  playground: '/account/api/$appId/playground',
} as const
type Section = keyof typeof SECTION_ROUTES

export const Route = createFileRoute('/_app/account/api/$appId')({
  context: ({ params }) => ({ appQueryOptions: trpc.apps.getOne.queryOptions(params.appId) }),
  loader: ({ context }) => {
    context.queryClient.query(context.appQueryOptions).catch(noop)
  },
  component: RouteComponent,
  pendingComponent: AppDetailPending,
})

function AppDetailPending() {
  return (
    <div className="flex flex-col gap-5">
      {/* Segmented tab bar */}
      <Skeleton className="h-9 w-full max-w-md rounded-lg" />
      {/* Selected section (settings is the default child) */}
      <div className="flex flex-col gap-3">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-44 rounded-xl" />
      </div>
    </div>
  )
}

function RouteComponent() {
  const { t } = useLingui()
  const { appId } = Route.useParams()
  const { data: app } = useSuspenseQuery(Route.useRouteContext().appQueryOptions)

  // Active state is driven by `<SegmentedTab>`'s underlying `<Link>` (each section is a distinct
  // child route), so no manual derivation is needed; the bare `$appId` index redirects to settings.
  const tabs: { key: Section; icon: typeof SettingsIcon; label: string; count?: number }[] = [
    { key: 'settings', icon: SettingsIcon, label: t`Settings` },
    { key: 'applicants', icon: UserIcon, label: t`Applicants`, count: app.applicants.total },
    { key: 'companies', icon: BuildingIcon, label: t`Companies`, count: app.companies.total },
    { key: 'jobs', icon: BriefcaseIcon, label: t`Jobs`, count: app.jobs.total },
    { key: 'playground', icon: FlaskConicalIcon, label: t`Playground` },
  ]

  return (
    <div className="flex flex-col gap-5">
      <SegmentedControl>
        {tabs.map(({ key, icon: Icon, label, count }) => (
          <SegmentedTab key={key} to={SECTION_ROUTES[key]} params={{ appId }}>
            <Icon className="size-3.5" />
            <span>{label}</span>
            {count != null && count > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 min-w-5 px-1.5 text-xs">
                {count}
              </Badge>
            )}
          </SegmentedTab>
        ))}
      </SegmentedControl>

      <Outlet />
    </div>
  )
}
